import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";

const CurrencyCtx = createContext(null);

export function CurrencyProvider({ children }) {
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [settings, setSettings] = useState({
    default_currency: "RWF",
    default_country: "RW",
    commission_rate: 0.1,
    platform_name: "RydeXpress Rwanda",
  });
  const [selectedCurrency, setSelectedCurrency] = useState(
    () => localStorage.getItem("display_currency") || "RWF"
  );
  const ratesCache = useRef({});

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: co }, { data: s }] = await Promise.all([
        api.get("/currencies"),
        api.get("/countries"),
        api.get("/settings/public"),
      ]);
      setCurrencies(c);
      setCountries(co);
      setSettings(s);
      if (!localStorage.getItem("display_currency")) {
        setSelectedCurrency(s.default_currency);
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changeCurrency = (code) => {
    setSelectedCurrency(code);
    localStorage.setItem("display_currency", code);
  };

  // Fetch and cache live exchange rate, returns a number (rate) or null while loading
  const getRate = useCallback(async (from, to) => {
    if (from === to) return 1;
    const key = `${from}_${to}`;
    const cached = ratesCache.current[key];
    const ONE_HOUR = 60 * 60 * 1000;
    if (cached && Date.now() - cached.fetchedAt < ONE_HOUR) {
      return cached.rate;
    }
    try {
      const { data } = await api.get("/exchange-rate", { params: { from_currency: from, to_currency: to } });
      const rate = data?.rate ?? 1;
      ratesCache.current[key] = { rate, fetchedAt: Date.now() };
      return rate;
    } catch (e) {
      return cached?.rate ?? 1;
    }
  }, []);

  // Convert a sync amount using only cached rate (returns null if not cached yet, triggers background fetch)
  const convertSync = useCallback((amount, from, to) => {
    if (from === to) return amount;
    const key = `${from}_${to}`;
    const cached = ratesCache.current[key];
    const ONE_HOUR = 60 * 60 * 1000;
    if (cached && Date.now() - cached.fetchedAt < ONE_HOUR) {
      return amount * cached.rate;
    }
    // trigger background fetch for next render
    getRate(from, to);
    return null;
  }, [getRate]);

  return (
    <CurrencyCtx.Provider value={{ currencies, countries, settings, selectedCurrency, changeCurrency, reload: load, getRate, convertSync }}>
      {children}
    </CurrencyCtx.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyCtx);
}
